import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Power, PowerOff, ArrowRight } from "lucide-react";

const blank = {
    name: "", title_keywords: "", description: "",
    required_skills: "", nice_to_have_skills: "",
    must_haves: "", red_flags: "",
    active: true,
};

const toCsv = (a) => (Array.isArray(a) ? a.join(", ") : (a || ""));
const fromCsv = (s) => String(s || "").split(/[,\n]/).map((x) => x.trim()).filter(Boolean);

export default function SeedsPage() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/seeds");
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (s) => {
        if (!window.confirm(`Delete seed "${s.name}"? Future jobs will no longer auto-apply this template.`)) return;
        try { await api.delete(`/seeds/${s.id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const toggleActive = async (s) => {
        try { await api.patch(`/seeds/${s.id}`, { active: !s.active }); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">JD Seeding</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Org-wide JD standards</h1>
                        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                            Define title-keyword–matched templates that every new job inherits. When a recruiter types a matching title, they'll see a checkbox to bake the seed in directly OR pass it to the AI generator. Bulk job creation auto-applies seeds without prompting.
                        </p>
                    </div>
                    <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="seed-new-btn">
                        <Plus className="w-4 h-4 mr-2" /> New seed
                    </Button>
                </div>
                {loading ? (
                    <div className="border border-dashed border-border p-16 text-center text-sm text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border p-16 text-center" data-testid="seeds-empty">
                        <Sparkles className="w-6 h-6 text-ikb mx-auto mb-3" />
                        <h3 className="font-display font-bold text-xl">No seeds yet</h3>
                        <p className="text-sm text-muted-foreground mt-1">Create your first one — e.g. "Engineering Standard" for any title containing "engineer" or "developer".</p>
                    </div>
                ) : (
                    <div className="grid gap-3" data-testid="seeds-list">
                        {items.map((s) => (
                            <div key={s.id} className={`border border-border bg-white p-4 ${s.active ? "" : "opacity-60"}`} data-testid={`seed-row-${s.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-display font-bold text-base">{s.name}</h3>
                                            {!s.active && <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5">Inactive</span>}
                                        </div>
                                        <div className="text-xs font-mono text-foreground/70 mt-1">
                                            Triggers on: {(s.title_keywords || []).length ? s.title_keywords.map((k) => `"${k}"`).join(", ") : <span className="text-foreground/40">any title</span>}
                                        </div>
                                        <div className="text-xs text-foreground/80 mt-2 line-clamp-2">{s.description || <span className="text-foreground/40">No description copy</span>}</div>
                                        <div className="text-[11px] font-mono text-foreground/60 mt-2 flex flex-wrap gap-3">
                                            <span>{(s.required_skills || []).length} req skills</span>
                                            <span>{(s.nice_to_have_skills || []).length} nice-to-haves</span>
                                            <span>{(s.must_haves || []).length} must-haves</span>
                                            <span>{(s.red_flags || []).length} red flags</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => nav(`/jobs/new?seed=${s.id}`)} className="p-2 border border-border hover:bg-secondary text-ikb inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-2" title="Start a new job from this seed" data-testid={`seed-use-${s.id}`}>
                                            <ArrowRight className="w-3.5 h-3.5" /> Use
                                        </button>
                                        <button onClick={() => toggleActive(s)} className="p-2 border border-border hover:bg-secondary" title={s.active ? "Deactivate" : "Activate"} data-testid={`seed-toggle-${s.id}`}>
                                            {s.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                                        </button>
                                        <button onClick={() => { setEditing(s); setOpen(true); }} className="p-2 border border-border hover:bg-secondary" title="Edit" data-testid={`seed-edit-${s.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                        <button onClick={() => remove(s)} className="p-2 border border-border hover:bg-rose-50 hover:text-rose-700" title="Delete" data-testid={`seed-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <SeedEditor open={open} onClose={() => setOpen(false)} seed={editing} onSaved={load} />
        </>
    );
}

function SeedEditor({ open, onClose, seed, onSaved }) {
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (seed) {
            setForm({
                name: seed.name || "",
                title_keywords: toCsv(seed.title_keywords),
                description: seed.description || "",
                required_skills: toCsv(seed.required_skills),
                nice_to_have_skills: toCsv(seed.nice_to_have_skills),
                must_haves: toCsv(seed.must_haves),
                red_flags: toCsv(seed.red_flags),
                active: seed.active !== false,
            });
        } else {
            setForm(blank);
        }
    }, [seed, open]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                title_keywords: fromCsv(form.title_keywords),
                description: form.description || "",
                required_skills: fromCsv(form.required_skills),
                nice_to_have_skills: fromCsv(form.nice_to_have_skills),
                must_haves: form.must_haves.split("\n").map((s) => s.trim()).filter(Boolean),
                red_flags: form.red_flags.split("\n").map((s) => s.trim()).filter(Boolean),
                active: !!form.active,
            };
            if (seed) await api.patch(`/seeds/${seed.id}`, payload);
            else await api.post("/seeds", payload);
            toast.success(seed ? "Seed updated" : "Seed created");
            onSaved && onSaved();
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="seed-editor">
                <DialogHeader>
                    <DialogTitle>{seed ? "Edit seed" : "New JD seed"}</DialogTitle>
                    <DialogDescription>Seeds get auto-applied when a job's title contains any of the triggers.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Name *</Label>
                            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none h-10 mt-1" placeholder="e.g. Engineering Standard" data-testid="seed-name" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Title triggers (comma-separated, leave empty for all)</Label>
                            <Input value={form.title_keywords} onChange={(e) => set("title_keywords", e.target.value)} className="rounded-none h-10 mt-1" placeholder="engineer, developer, sde" data-testid="seed-keywords" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-mono">JD copy (Markdown / HTML)</Label>
                        <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="rounded-none mt-1" placeholder="All engineering roles must mention: code reviews, on-call rotation, RFC culture, etc." data-testid="seed-description" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Required skills (CSV)</Label>
                            <Input value={form.required_skills} onChange={(e) => set("required_skills", e.target.value)} className="rounded-none h-10 mt-1" placeholder="Git, CI/CD" data-testid="seed-required-skills" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Nice-to-haves (CSV)</Label>
                            <Input value={form.nice_to_have_skills} onChange={(e) => set("nice_to_have_skills", e.target.value)} className="rounded-none h-10 mt-1" placeholder="Mentorship, OSS contributions" data-testid="seed-nice-skills" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Must-haves (1 per line)</Label>
                            <Textarea value={form.must_haves} onChange={(e) => set("must_haves", e.target.value)} rows={3} className="rounded-none mt-1" placeholder={"Strong written communication\nComfortable in remote-first culture"} data-testid="seed-must-haves" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-mono">Red flags (1 per line)</Label>
                            <Textarea value={form.red_flags} onChange={(e) => set("red_flags", e.target.value)} rows={3} className="rounded-none mt-1" placeholder="More than 5 short stints (<12 months) in last 7 years" data-testid="seed-red-flags" />
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="seed-active" />
                        Active (will be considered for new jobs)
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="seed-save">{busy ? "Saving…" : (seed ? "Save" : "Create →")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
